import {useCallback, useContext} from 'react'
import {useDropzone} from 'react-dropzone'
import "react-dropzone/examples/theme.css";
import { NotificationContextType } from "../../types";
import { NotificationContext } from "../../components/NotificationPane";
import { useApi } from "../../hooks/useApi";
import { colourOverrides } from "../../util/Colours";

type FileDropzoneProps = {
  onUploaded?: () => void;
}

// Every file goes to the one place the backend knows: the edb_megaloader input folder. There is
// no upload type to pick -- the megaloader routes by its rules, not by folder (2026-09-16).
function FileDropzone({onUploaded}: FileDropzoneProps) {
  const { uploadFile } = useApi();
  const { pushNotification } = useContext<NotificationContextType | null>(NotificationContext)!;

  const upload = useCallback(async (file: File, showSuccess: boolean) => {
    try {
      /* --- bytes --- */
      const buffer = await file.arrayBuffer();
      const byteArray = Array.from(new Uint8Array(buffer));

      /* --- timestamps --- */
      // File.lastModified is a DOM-High-Res Time Stamp (ms since epoch, local TZ)
      const lastWrite = new Date(file.lastModified);

      await uploadFile(file.name, byteArray, lastWrite.toISOString(), lastWrite.toISOString());

      if (showSuccess) {
        pushNotification(`File ${file.name} uploaded successfully`, "success");
      }

      onUploaded?.();
      return true;
    } catch {
      pushNotification("Error uploading file", "error");
      return false;
    }
  }, [uploadFile, pushNotification, onUploaded]);

  /* --- react-dropzone --- */
  const onDrop = useCallback((accepted: File[]) => {
    const MAX = 5 * 1024 * 1024;
    const validFiles = accepted.filter((file) => {
      if (file.size > MAX) {
        pushNotification(`File ${file.name} exceeds the 5 MB limit`, "error");
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    const showSuccess = validFiles.length === 1;
    Promise.all(validFiles.map((file) => upload(file, showSuccess))).then((results) => {
      if (showSuccess) return;
      const successCount = results.filter(Boolean).length;
      if (successCount > 0) {
        pushNotification(`${successCount} files uploaded successfully`, "success");
      }
    });
  }, [upload, pushNotification]);

  const {getRootProps, getInputProps} = useDropzone({onDrop})

  // A short strip, not a panel: the theme's .dropzone is a column flexbox, so centre the one line
  // of text vertically and let the surrounding card fit round it (BaseTemplate.Body fitContent).
  return (
    <div {...getRootProps({className: 'dropzone'})}
         style={{ width: "100%", minHeight: "72px", padding: "12px 20px", justifyContent: "center",
                  backgroundColor: colourOverrides.grey['50'] }}>
      <input {...getInputProps()} />
      <p style={{fontSize: "16px", margin: 0, color: colourOverrides.grey['900']}}>
        Drag 'n' drop files here, or click to select files (max 5 MB each). They go straight to the loader's input folder.
      </p>
    </div>
  )
}

export default FileDropzone
